import { useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
  LayoutGrid, Inbox, CalendarClock, Film, Radio, Users, MessageSquare, Star, Library,
  Check, X, Edit3, Trash2, RefreshCw, Youtube, Play, Square, Sparkles, Wallet,
} from "lucide-react";
import { Payouts } from "./admin/Payouts";
import { TwoFactorCard } from "../components/TwoFactor";
import api from "../lib/api";
import ConsoleShell, { StatCard } from "../components/ConsoleShell";
import { CheckinPanel } from "./OrganizerPortal";
import { Spinner, EmptyState, StatusBadge, formatViews } from "../components/states";
import { toast } from "sonner";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { key: "recordings", label: "Recordings", icon: Inbox },
  { key: "bookings", label: "Bookings", icon: CalendarClock },
  { key: "shows", label: "Shows", icon: Film },
  { key: "live", label: "Live Now", icon: Radio },
  { key: "djs", label: "DJs & Members", icon: Users },
  { key: "ticketing", label: "Events & Tickets", icon: CalendarClock },
  { key: "payouts", label: "Payouts", icon: Wallet },
  { key: "comments", label: "Comments", icon: MessageSquare },
  { key: "picks", label: "Picks", icon: Star },
  { key: "catalog", label: "Catalog", icon: Library },
];

export default function AdminConsole() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "dashboard";
  const setActive = (k) => setParams((p) => { const n = new URLSearchParams(p); if (k === "dashboard") n.delete("tab"); else n.set("tab", k); return n; });
  const [dash, setDash] = useState(null);
  const loadDash = useCallback(() => api.get("/admin/dashboard").then((r) => setDash(r.data)).catch(() => {}), []);
  useEffect(() => { loadDash(); }, [loadDash]);
  const badges = dash ? { recordings: dash.stats.pending_recordings, bookings: dash.stats.pending_bookings, djs: dash.stats.dj_applications, comments: dash.stats.reported } : {};

  return (
    <ConsoleShell title={titleFor(active)} subtitle="STUDIO CONTROL ROOM" nav={NAV} active={active} onNav={setActive} badges={badges} crumbs={[{ label: "Dashboard", to: "/dashboard" }, { label: "Admin", to: "/console" }, ...(active !== "dashboard" ? [{ label: titleFor(active) }] : [])]}>
      {active === "dashboard" && <Dashboard dash={dash} reload={loadDash} go={setActive} />}
      {active === "recordings" && <Recordings reload={loadDash} />}
      {active === "bookings" && <Bookings reload={loadDash} />}
      {active === "shows" && <Shows />}
      {active === "live" && <LiveControl reload={loadDash} />}
      {active === "djs" && <DjsMembers reload={loadDash} />}
      {active === "ticketing" && <Ticketing />}
      {active === "payouts" && <Payouts />}
      {active === "comments" && <Comments reload={loadDash} />}
      {active === "picks" && <CrudEntity entity="picks" fields={[["number", "Pick number"], ["curator", "Curator"], ["image", "Image URL"], ["quote", "Quote"]]} />}
      {active === "catalog" && <Catalog />}
    </ConsoleShell>
  );
}

const titleFor = (k) => ({ dashboard: "Dashboard", recordings: "Recordings", bookings: "Bookings", shows: "Shows", live: "Live Now", djs: "DJs & Members", payouts: "Payouts", comments: "Comments", picks: "Picks", catalog: "Catalog" }[k]);

function Dashboard({ dash, reload, go }) {
  if (!dash) return <Spinner />;
  const s = dash.stats;
  return (
    <div className="space-y-6">
      <div className="card-copper rounded-xl p-4 max-w-xl"><p className="mono-label text-[#71717A] mb-2">ADMIN SECURITY</p><TwoFactorCard /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Pending Recordings" value={s.pending_recordings} icon={Inbox} testid="stat-pending-recordings" onClick={() => go("recordings")} />
        <StatCard label="Pending Bookings" value={s.pending_bookings} icon={CalendarClock} testid="stat-pending-bookings" onClick={() => go("bookings")} />
        <StatCard label="DJ Applications" value={s.dj_applications} icon={Users} testid="stat-dj-apps" onClick={() => go("djs")} />
        <StatCard label="Published Shows" value={s.published_shows} icon={Film} testid="stat-published" />
        <StatCard label="Total Views" value={formatViews(s.total_views)} icon={Play} testid="stat-views" />
        <StatCard label="Members" value={s.members} icon={Star} testid="stat-members" />
        <StatCard label="Users" value={s.users} icon={Users} testid="stat-users" />
        <StatCard label="Comments" value={s.comments} icon={MessageSquare} testid="stat-comments" onClick={() => go("comments")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-copper rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><span className="font-display font-bold uppercase text-sm">On Air</span><button onClick={() => go("live")} className="mono-label text-[#E8552A]">MANAGE →</button></div>
          {dash.live?.id ? (
            <div className="flex items-center gap-3">
              <span className="live-pulse w-2.5 h-2.5 rounded-full bg-[#10B981]" />
              <div><StatusBadge status="live" /><p className="font-display font-semibold mt-1">{dash.live.title}</p></div>
            </div>
          ) : <p className="text-sm text-[#71717A]">Nothing broadcasting.</p>}
        </div>
        <div className="card-copper rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><span className="font-display font-bold uppercase text-sm">Review Queue</span><button onClick={() => go("recordings")} className="mono-label text-[#E8552A]">ALL →</button></div>
          {dash.review_queue.length === 0 ? <p className="text-sm text-[#71717A]">Queue is clear.</p> : dash.review_queue.slice(0, 4).map((r) => (
            <div key={r.id} className="flex items-center justify-between py-1.5"><span className="text-sm truncate">{r.title}</span><StatusBadge status={r.status} /></div>
          ))}
        </div>
        <div className="card-copper rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><span className="font-display font-bold uppercase text-sm">Slot Requests</span><button onClick={() => go("bookings")} className="mono-label text-[#E8552A]">ALL →</button></div>
          {dash.slot_requests.length === 0 ? <p className="text-sm text-[#71717A]">No requests.</p> : dash.slot_requests.slice(0, 4).map((b) => (
            <div key={b.id} className="flex items-center justify-between py-1.5"><div><p className="text-sm">{b.dj_name}</p><p className="mono-label text-[#71717A]">{b.preferred_date} · {b.preferred_time}</p></div><StatusBadge status={b.status} /></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      {tabs.map((t) => (
        <button key={t} data-testid={`tab-${t}`} onClick={() => onChange(t)} className={`mono-label rounded-full px-4 py-1.5 border ${value === t ? "border-[#E8552A] text-[#E8552A]" : "border-[#27272A] text-[#A1A1AA]"}`}>{t.toUpperCase()}</button>
      ))}
    </div>
  );
}

function Recordings({ reload }) {
  const [status, setStatus] = useState("submitted");
  const [items, setItems] = useState(null);
  const load = useCallback(() => { setItems(null); api.get(`/admin/recordings?status=${status}`).then((r) => setItems(r.data)); }, [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    let notes = "";
    if (decision !== "approve") { notes = prompt(`Notes for ${decision}:`) || ""; }
    try { await api.patch(`/admin/recordings/${id}/review`, { decision, notes }); toast.success(`Recording ${decision}d`); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const retry = async (id) => { try { await api.post(`/admin/recordings/${id}/youtube/retry`); toast.success("YouTube retry started"); setTimeout(load, 800); } catch { toast.error("Failed"); } };
  const [summaries, setSummaries] = useState({});
  const summarize = async (id) => {
    setSummaries((s) => ({ ...s, [id]: "…" }));
    try { const { data } = await api.post(`/ai/review-summary/${id}`); setSummaries((s) => ({ ...s, [id]: data.summary })); }
    catch (e) { setSummaries((s) => ({ ...s, [id]: null })); toast.error(e.response?.data?.detail || "AI unavailable"); }
  };

  return (
    <div>
      <Tabs tabs={["submitted", "review", "processing", "published", "rejected", "all"]} value={status} onChange={setStatus} />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="Review queue is empty. Nice work." /> : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`admin-rec-${r.id}`} className="card-copper rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <img src={r.cover_image} alt="" className="w-full sm:w-28 h-28 sm:h-16 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold truncate">{r.title}</p>
                  <p className="mono-label text-[#71717A]">{r.dj_name} · {r.submission_type} · {(r.genres || []).join(", ")}</p>
                  <div className="flex items-center gap-2 mt-1"><StatusBadge status={r.status} /><span className="mono-label text-[#71717A] flex items-center gap-1"><Youtube size={11} /> {r.youtube_status}</span></div>
                </div>
                <div className="flex gap-2">
                  {(r.status === "submitted" || r.status === "review") && (
                    <>
                      <button data-testid={`ai-summary-${r.id}`} onClick={() => summarize(r.id)} className="border border-[#E8552A]/40 text-[#E8552A] rounded-lg px-3 py-2 mono-label flex items-center gap-1"><Sparkles size={13} /> AI</button>
                      <button data-testid={`approve-${r.id}`} onClick={() => review(r.id, "approve")} className="copper-btn rounded-lg px-3 py-2 mono-label flex items-center gap-1"><Check size={13} /> APPROVE</button>
                      <button data-testid={`changes-${r.id}`} onClick={() => review(r.id, "changes")} className="border border-[#FF9F1C]/40 text-[#FF9F1C] rounded-lg px-3 py-2 mono-label"><Edit3 size={13} /></button>
                      <button data-testid={`reject-${r.id}`} onClick={() => review(r.id, "reject")} className="border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-3 py-2 mono-label"><X size={13} /></button>
                    </>
                  )}
                  {r.youtube_status === "Failed" && <button onClick={() => retry(r.id)} className="border border-[#E8552A]/40 text-[#E8552A] rounded-lg px-3 py-2 mono-label flex items-center gap-1"><RefreshCw size={13} /> RETRY</button>}
                </div>
              </div>
              {(summaries[r.id] || r.ai_summary) && <div data-testid={`ai-summary-text-${r.id}`} className="mt-3 rounded-lg border border-[#E8552A]/20 bg-[#E8552A]/5 p-3 text-sm text-[#D4D4D8] whitespace-pre-wrap"><span className="mono-label text-[#E8552A]">CLAUDE REVIEW SUMMARY</span><br />{summaries[r.id] || r.ai_summary}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bookings({ reload }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { setItems(null); api.get("/admin/bookings").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const STATUSES = ["requested", "review", "confirmed", "scheduled", "recorded", "processing", "published", "rejected"];
  const update = async (id, status) => { try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success("Updated"); load(); reload(); } catch { toast.error("Failed"); } };
  if (!items) return <Spinner />;
  if (items.length === 0) return <EmptyState title="No bookings" icon={CalendarClock} />;
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <div key={b.id} data-testid={`admin-booking-${b.id}`} className="card-copper rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1"><p className="font-display font-semibold">{b.dj_name} · {b.recording_type}</p><p className="mono-label text-[#71717A] mt-1">{b.preferred_date} · {b.preferred_time} · {b.duration} · {b.genre}</p><p className="text-sm text-[#A1A1AA] mt-1">{b.description}</p></div>
            <div className="flex items-center gap-2">
              <StatusBadge status={b.status} />
              <select data-testid={`booking-status-${b.id}`} value={b.status} onChange={(e) => update(b.id, e.target.value)} className="bg-[#0A0A0C] border border-[#27272A] rounded-lg px-2 py-2 text-xs mono-label outline-none focus:border-[#E8552A]">
                {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LiveControl({ reload }) {
  const [live, setLive] = useState(null);
  const [f, setF] = useState({ title: "", dj_name: "", genres: "", cover_image: "" });
  const load = useCallback(() => api.get("/live").then((r) => setLive(r.data?.id ? r.data : null)), []);
  useEffect(() => { load(); }, [load]);
  const start = async () => { try { await api.post("/admin/live", { ...f, genres: f.genres.split(",").map((x) => x.trim()).filter(Boolean) }); toast.success("Live started"); load(); reload(); } catch { toast.error("Failed"); } };
  const end = async () => { try { await api.patch("/admin/live/end"); toast.success("Broadcast ended"); load(); reload(); } catch { toast.error("Failed"); } };
  const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A]";
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-copper rounded-xl p-6">
        <h3 className="font-display font-bold uppercase mb-4">Current Broadcast</h3>
        {live ? (
          <div>
            <span className="live-pulse inline-flex items-center gap-2 rounded-md border border-[#10B981]/50 bg-[#10B981]/10 px-3 py-1.5"><Radio size={13} className="text-[#10B981]" /><span className="mono-label text-[#10B981]">LIVE</span></span>
            <p className="font-display text-xl font-bold mt-3">{live.title}</p>
            <p className="mono-label text-[#71717A] mt-1">{live.dj_name} · {formatViews(live.viewers)} viewers</p>
            <button data-testid="end-live-btn" onClick={end} className="mt-5 border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-4 py-2.5 mono-label flex items-center gap-2"><Square size={13} /> END BROADCAST</button>
          </div>
        ) : <EmptyState title="Nothing broadcasting" sub="Start a live session on the right." icon={Radio} />}
      </div>
      <div className="card-copper rounded-xl p-6">
        <h3 className="font-display font-bold uppercase mb-4">Start / Configure Live</h3>
        <div className="space-y-3">
          <input data-testid="live-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Broadcast title" className={inp} />
          <input data-testid="live-dj" value={f.dj_name} onChange={(e) => setF({ ...f, dj_name: e.target.value })} placeholder="DJ name" className={inp} />
          <input data-testid="live-genres" value={f.genres} onChange={(e) => setF({ ...f, genres: e.target.value })} placeholder="Genres (comma separated)" className={inp} />
          <input data-testid="live-cover" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} placeholder="Cover image URL" className={inp} />
          <input data-testid="live-stream-url" value={f.stream_url || ""} onChange={(e) => setF({ ...f, stream_url: e.target.value })} placeholder="Stream URL (HLS .m3u8 / Icecast / MP4) — required for playback" className={inp} />
          <button data-testid="start-live-btn" onClick={start} className="w-full copper-btn rounded-lg py-3 mono-label flex items-center justify-center gap-2"><Play size={14} /> GO LIVE</button>
        </div>
      </div>
    </div>
  );
}

function Ticketing() {
  const [orgs, setOrgs] = useState([]);
  const [events, setEvents] = useState([]);
  const [pays, setPays] = useState([]);
  const [scan, setScan] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/organizers").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/admin/events?status=all").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/payments/admin/all?status=pending_verification").then((r) => setPays(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const setOrg = async (id, status) => { try { await api.patch(`/admin/organizers/${id}/status`, { status }); toast.success(`Organizer ${status}`); load(); } catch { toast.error("Failed"); } };
  const setEv = async (id, status) => { const notes = status === "rejected" ? prompt("Reason:") || "" : ""; try { await api.patch(`/admin/events/${id}/status`, { status, notes }); toast.success(`Event ${status}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const verify = async (id, approve) => { try { await api.post(`/payments/admin/${id}/verify-utr`, { approve }); toast.success(approve ? "Payment verified" : "UTR rejected"); load(); } catch { toast.error("Failed"); } };
  const B = ({ id, onClick, children, danger }) => <button data-testid={id} onClick={onClick} className={`rounded-lg px-3 py-1.5 mono-label border ${danger ? "border-[#ef4444]/40 text-[#ef4444]" : "copper-btn border-transparent"}`}>{children}</button>;
  return (
    <div className="space-y-8">
      <section><h3 className="font-display font-bold uppercase mb-3">Organizer Applications</h3>
        {orgs.length === 0 ? <EmptyState title="No organizers yet" /> : orgs.map((o) => (
          <div key={o.id} data-testid={`org-${o.id}`} className="card-copper rounded-xl p-4 mb-2 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-display font-semibold">{o.org_name || o.name}</p><p className="mono-label text-[#71717A]">{o.email} · {o.location}</p></div>
            <div className="flex items-center gap-2"><StatusBadge status={o.organizer_status} />
              {o.organizer_status !== "approved" && <B id={`org-approve-${o.id}`} onClick={() => setOrg(o.id, "approved")}>APPROVE</B>}
              {o.organizer_status === "approved" && <B id={`org-suspend-${o.id}`} danger onClick={() => setOrg(o.id, "suspended")}>SUSPEND</B>}
              {o.organizer_status === "pending" && <B id={`org-reject-${o.id}`} danger onClick={() => setOrg(o.id, "rejected")}>REJECT</B>}</div>
          </div>))}
      </section>
      <section><h3 className="font-display font-bold uppercase mb-3">Events</h3>
        {events.length === 0 ? <EmptyState title="No events" /> : events.map((e) => (
          <div key={e.id} data-testid={`adm-event-${e.id}`} className="card-copper rounded-xl p-4 mb-2 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-display font-semibold">{e.title}</p><p className="mono-label text-[#71717A]">{e.organizer_name} · {new Date(e.date).toLocaleDateString("en-IN")} · {e.ticket_types.reduce((s, t) => s + (t.sold || 0), 0)} sold</p></div>
            <div className="flex items-center gap-2 flex-wrap"><StatusBadge status={e.status} />
              {e.status === "pending" && <><B id={`ev-approve-${e.id}`} onClick={() => setEv(e.id, "published")}>APPROVE & PUBLISH</B><B id={`ev-reject-${e.id}`} danger onClick={() => setEv(e.id, "rejected")}>REJECT</B></>}
              {e.status === "published" && <><B id={`ev-checkin-${e.id}`} onClick={() => setScan(e)}>CHECK-IN</B><B id={`ev-postpone-${e.id}`} danger onClick={() => setEv(e.id, "postponed")}>POSTPONE</B><B id={`ev-cancel-${e.id}`} danger onClick={() => setEv(e.id, "cancelled")}>CANCEL</B></>}</div>
          </div>))}
      </section>
      <section><h3 className="font-display font-bold uppercase mb-3">UPI Payments Awaiting Verification</h3>
        {pays.length === 0 ? <p className="mono-label text-[#71717A]">NO DATA AVAILABLE</p> : pays.map((p) => (
          <div key={p.id} data-testid={`utr-${p.id}`} className="card-copper rounded-xl p-4 mb-2 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-display font-semibold">{p.user_email} · ₹{(p.amount / 100).toLocaleString("en-IN")}</p><p className="mono-label text-[#71717A]">{p.tier_name} · UTR {p.utr}</p></div>
            <div className="flex gap-2"><B id={`utr-ok-${p.id}`} onClick={() => verify(p.id, true)}>VERIFIED IN BANK</B><B id={`utr-no-${p.id}`} danger onClick={() => verify(p.id, false)}>REJECT</B></div>
          </div>))}
      </section>
      {scan && <CheckinPanel event={scan} onClose={() => setScan(null)} />}
    </div>
  );
}

function DjsMembers({ reload }) {
  const [tab, setTab] = useState("djs");
  const [djs, setDjs] = useState(null);
  const [users, setUsers] = useState(null);
  const load = useCallback(() => { api.get("/admin/djs").then((r) => setDjs(r.data)); api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "USER"))); }, []);
  useEffect(() => { load(); }, [load]);
  const djStatus = async (id, status) => { try { await api.patch(`/admin/djs/${id}/status`, { status }); toast.success(`DJ ${status}`); load(); reload(); } catch { toast.error("Failed"); } };
  const userStatus = async (id, status) => { try { await api.patch(`/admin/users/${id}/status`, { status }); toast.success(`User ${status}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div>
      <Tabs tabs={["djs", "users"]} value={tab} onChange={setTab} />
      {tab === "djs" ? (!djs ? <Spinner /> : djs.length === 0 ? <EmptyState title="No DJs" icon={Users} /> : (
        <div className="space-y-3">
          {djs.map((d) => (
            <div key={d.id} data-testid={`admin-dj-${d.id}`} className="card-copper rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <img src={d.profile_image} alt="" className="w-12 h-12 rounded-full object-cover" />
              <div className="flex-1"><p className="font-display font-semibold">{d.stage_name || d.name}</p><p className="mono-label text-[#71717A]">{d.email} · {(d.genres || []).join(", ")} · {d.location}</p></div>
              <StatusBadge status={d.dj_status} />
              <div className="flex gap-2">
                {d.dj_status !== "approved" && <button data-testid={`approve-dj-${d.id}`} onClick={() => djStatus(d.id, "approved")} className="copper-btn rounded-lg px-3 py-2 mono-label">APPROVE</button>}
                {d.dj_status !== "rejected" && <button data-testid={`reject-dj-${d.id}`} onClick={() => djStatus(d.id, "rejected")} className="border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-3 py-2 mono-label">REJECT</button>}
              </div>
            </div>
          ))}
        </div>
      )) : (!users ? <Spinner /> : users.length === 0 ? <EmptyState title="No users" icon={Users} /> : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} className="card-copper rounded-xl p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#E8552A]/20 flex items-center justify-center font-display font-bold text-[#E8552A]">{u.name?.[0]}</span>
              <div className="flex-1"><p className="font-display font-semibold">{u.name} {u.is_member && <span className="mono-label text-[#E8552A]">· MEMBER</span>}</p><p className="mono-label text-[#71717A]">{u.email}</p></div>
              <StatusBadge status={u.status} />
              <button data-testid={`suspend-${u.id}`} onClick={() => userStatus(u.id, u.status === "suspended" ? "active" : "suspended")} className="border border-[#27272A] rounded-lg px-3 py-2 mono-label hover:border-[#E8552A]">{u.status === "suspended" ? "REINSTATE" : "SUSPEND"}</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Comments({ reload }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/comments").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { try { await api.delete(`/admin/comments/${id}`); toast.success("Removed"); load(); reload(); } catch { toast.error("Failed"); } };
  if (!items) return <Spinner />;
  if (items.length === 0) return <EmptyState title="No comments" icon={MessageSquare} />;
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} data-testid={`admin-comment-${c.id}`} className="card-copper rounded-xl p-4 flex items-start justify-between gap-3">
          <div><span className="mono-label text-[#E8552A]">{c.handle}</span><p className="text-sm text-[#D4D4D8] mt-1">{c.body}</p><StatusBadge status={c.status} /></div>
          {c.status !== "removed" && <button data-testid={`remove-comment-${c.id}`} onClick={() => remove(c.id)} className="border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-3 py-2 mono-label"><Trash2 size={13} /></button>}
        </div>
      ))}
    </div>
  );
}

function Shows() {
  return <CrudEntity entity="shows" fields={[["title", "Show title"], ["dj_name", "DJ name"], ["cover_image", "Cover image URL"], ["genres", "Genres (comma sep)"], ["description", "Description"]]} arrayFields={["genres"]} />;
}

export function CrudEntity({ entity, fields, arrayFields = [] }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const pub = entity === "shows" ? "recordings" : entity;
  const load = useCallback(() => api.get(`/${pub === "recordings" ? "recordings" : pub}`).then((r) => setItems(r.data)).catch(() => setItems([])), [pub]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...form };
    arrayFields.forEach((k) => { if (typeof body[k] === "string") body[k] = body[k].split(",").map((x) => x.trim()).filter(Boolean); });
    if (entity === "picks" && body.number) body.number = Number(body.number);
    try { await api.post(`/admin/${entity}`, body); toast.success("Created"); setOpen(false); setForm({}); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/admin/${entity}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div>
      <button data-testid={`add-${entity}`} onClick={() => setOpen(!open)} className="copper-btn rounded-full px-4 py-2 mono-label mb-4">+ ADD {entity.toUpperCase()}</button>
      {open && (
        <div className="card-copper rounded-xl p-5 mb-5 space-y-3">
          {fields.map(([k, ph]) => (
            <input key={k} data-testid={`field-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#E8552A]" />
          ))}
          <button data-testid={`save-${entity}`} onClick={save} className="copper-btn rounded-lg px-5 py-2.5 mono-label">SAVE</button>
        </div>
      )}
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title={`No ${entity} yet`} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="card-copper rounded-xl p-4">
              {(it.cover_image || it.image) && <img src={it.cover_image || it.image} alt="" className="w-full h-28 object-cover rounded-lg mb-3" />}
              <p className="font-display font-semibold truncate">{it.title || it.curator || it.name || `#${it.number}`}</p>
              <p className="mono-label text-[#71717A] truncate">{it.dj_name || it.quote || (it.genres || []).join(", ")}</p>
              <button data-testid={`delete-${it.id}`} onClick={() => del(it.id)} className="mt-3 border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-3 py-1.5 mono-label flex items-center gap-1"><Trash2 size={12} /> DELETE</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Catalog() {
  return (
    <div className="space-y-10">
      <div><h3 className="font-display text-lg font-bold uppercase mb-4 text-[#E8552A]">Artists</h3><CrudEntity entity="artists" fields={[["name", "Artist name"], ["image", "Image URL"], ["genres", "Genres (comma sep)"], ["location", "Location"], ["bio", "Bio"]]} arrayFields={["genres"]} /></div>
      <div><h3 className="font-display text-lg font-bold uppercase mb-4 text-[#E8552A]">Events</h3><CrudEntity entity="events" fields={[["title", "Event title"], ["date", "Date (YYYY-MM-DD)"], ["venue", "Venue"], ["image", "Image URL"], ["price", "Price"], ["description", "Description"]]} /></div>
      <div><h3 className="font-display text-lg font-bold uppercase mb-4 text-[#E8552A]">Shop Products</h3><CrudEntity entity="products" fields={[["name", "Product name"], ["price", "Price"], ["category", "Category"], ["image", "Image URL"]]} /></div>
    </div>
  );
}
